import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Add, Circle, Settings, TableChart } from '@mui/icons-material';
import {
  Canvas as FabricCanvas,
  Text as FabricText,
  Rect as FabricRect,
  Group as FabricGroup,
  Image as FabricImage,
  Shadow as FabricShadow,
} from 'fabric';
import { DisplayData } from 'src/pages/Solutions/SolutionAssetHierarchy/SolutionAssetHierarchy';
import { PlacedProperty } from './TempImagePropertyMapper';

interface LiveMonitorTabProps {
  uploadedImage: string | null;
  availableProperties: DisplayData[];
  placedProperties: PlacedProperty[];
  onOpenConfigure: () => void;
  isConnected: boolean;
}

const TempLiveMonitorTab: React.FC<LiveMonitorTabProps> = ({
  uploadedImage,
  availableProperties,
  placedProperties,
  onOpenConfigure,
  isConnected,
}) => {
  const [lastUpdate, setLastUpdate] = useState<string>('');

  useEffect(() => {
    const now = new Date();
    setLastUpdate(now.toLocaleTimeString());
  }, [availableProperties]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
    padding: 0,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const imageContainerStyle: React.CSSProperties = {
    position: 'relative',
    backgroundColor: 'white',
    overflow: 'auto',
    display: 'block',
    padding: 0,
    margin: 0,
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<FabricCanvas | null>(null);
  const CANVAS_WIDTH = 1500;
  const CANVAS_HEIGHT = 800;

  const noImageStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#999',
    fontSize: '18px',
  };

  const visiblePlacedProperties = placedProperties.filter((prop) => {
    const availableProp = availableProperties.find((ap) => ap.key === prop.key);
    return prop.visible !== false && availableProp?.visible !== false;
  });

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    if (fabricRef.current) {
      fabricRef.current.dispose();
    }
    const canvas = new FabricCanvas(canvasRef.current, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      backgroundColor: '#fff',
      selection: false,
    });
    fabricRef.current = canvas;
    return () => {
      canvas.dispose();
    };
  }, []);

  const imageObjRef = useRef<FabricImage | null>(null);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (imageObjRef.current) {
      canvas.remove(imageObjRef.current);
      imageObjRef.current = null;
    }

    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'text' && obj.get('data') === 'no-image') {
        canvas.remove(obj);
      }
    });
    if (uploadedImage) {
      FabricImage.fromURL(uploadedImage, {})
        .then((img) => {
          const scale = Math.min(
            CANVAS_WIDTH / (img.width || 1),
            CANVAS_HEIGHT / (img.height || 1)
          );

          const finalWidth = img.width * scale;
          const finalHeight = img.height * scale;

          canvas.setWidth(finalWidth);
          canvas.setHeight(finalHeight);

          img.set({
            left: 0,
            top: 0,
            scaleX: scale,
            scaleY: scale,
            selectable: false,
            evented: false,
          });
          imageObjRef.current = img;
          canvas.add(img);
          canvas.sendObjectToBack(img);
          canvas.renderAll();
        })
        .catch((err) => {
          console.error('Failed to load image in live monitor canvas:', err);
        });
    } else {
      const text = new FabricText('No image uploaded. Please configure.', {
        left: CANVAS_WIDTH / 2,
        top: CANVAS_HEIGHT / 2,
        originX: 'center',
        originY: 'center',
        fontSize: 28,
        fill: '#999',
        fontWeight: 'bold',
        selectable: false,
        evented: false,
      });
      text.set('data', 'no-image');
      canvas.add(text);
      canvas.renderAll();
    }
  }, [uploadedImage]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'group') {
        canvas.remove(obj);
      }
      if (obj.type === 'text' && obj.get('data') !== 'no-image') {
        canvas.remove(obj);
      }
    });

    visiblePlacedProperties.forEach((property) => {
      const label = `${property.key}: ${property.value}`;
      const fontSize = 12;
      const fontFamily = 'Inter, sans-serif';
      const fontWeight = 500;
      const paddingX = 12;
      const paddingY = 6;
      const borderRadius = 16;

      const textObj = new FabricText(label, {
        fontSize,
        fontFamily,
        fontWeight,
        fill: '#FFF',
        originX: 'center',
        originY: 'center',
        shadow: new FabricShadow({
          color: 'rgba(0,0,0,0.15)',
          offsetX: 0,
          offsetY: 2,
        }),
      });

      const textWidth = textObj.width || 0;
      const textHeight = textObj.height || 0;
      const badgeWidth = textWidth + paddingX * 2;
      const badgeHeight = textHeight + paddingY * 2;

      const rect = new FabricRect({
        width: badgeWidth,
        height: badgeHeight,
        rx: borderRadius,
        ry: borderRadius,
        fill: '#1976d2',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      });

      const badgeGroup = new FabricGroup([rect, textObj], {
        left: property.x,
        top: property.y,
        originX: 'center',
        originY: 'center',
        selectable: false,
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        hoverCursor: 'default',
        scaleX: 1,
        scaleY: 1,
      });
      canvas.add(badgeGroup);
    });

    if (imageObjRef.current) {
      canvas.sendObjectToBack(imageObjRef.current);
    }
    canvas.renderAll();
  }, [visiblePlacedProperties, placedProperties, availableProperties]);

  return (
    <Box sx={containerStyle}>
      <Box sx={headerStyle}>
        <Box>
          <Typography
            variant="subtitle1"
            style={{ color: '#333', marginBottom: '4px' }}
          >
            Live Monitor
            <Circle
              sx={{
                marginLeft: '10px',
                fontSize: '12px',
                color: isConnected ? '#4caf50' : '#f44336',
              }}
            />
          </Typography>
          <Typography variant="caption" style={{ color: '#666' }}>
            Last updated: {lastUpdate || 'Never'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Settings />}
          onClick={onOpenConfigure}
          sx={{
            textTransform: 'none',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: '#fff',
            },
          }}
        >
          Configure
        </Button>
      </Box>
      <Box sx={imageContainerStyle}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', margin: 0, padding: 0 }}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          data-canvas="true"
        />
      </Box>
    </Box>
  );
};

export default TempLiveMonitorTab;
